import { Component } from '@theme/component';
import { WISHLIST_STORAGE_KEY, WishlistEvents, isInWishlist, toggleWishlist } from '@theme/wishlist';

/**
 * A save/remove toggle for a single product. Used on product cards, the PDP,
 * and the wishlist page. Reflects the saved state from localStorage on connect
 * and keeps in sync across instances and tabs.
 *
 * Markup is provided by `snippets/wishlist-button.liquid`.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} button - The clickable toggle.
 * @extends {Component<Refs>}
 */
class WishlistButton extends Component {
  // No requiredRefs: async-injected cards (product recommendations) can connect
  // this component before the inner button ref registers, and Component throws
  // on a missing required ref. #render() null-guards the button instead, and
  // the base mutation observer picks it up once present.

  /** @returns {string} */
  get handle() {
    return this.dataset.productHandle ?? '';
  }

  /** @returns {string} */
  get productTitle() {
    return this.dataset.productTitle || 'this product';
  }

  connectedCallback() {
    super.connectedCallback();
    this.#render();
    document.addEventListener(WishlistEvents.update, this.#onUpdate);
    window.addEventListener('storage', this.#onStorage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(WishlistEvents.update, this.#onUpdate);
    window.removeEventListener('storage', this.#onStorage);
  }

  /**
   * Declarative `on:click="/toggle"` handler.
   * @param {Event} [event]
   */
  toggle = (event) => {
    // Stop the click bubbling to a parent product-card link / view transition.
    event?.preventDefault?.();
    event?.stopPropagation?.();
    toggleWishlist(this.handle);
  };

  #onUpdate = () => this.#render();

  /** @param {StorageEvent} event */
  #onStorage = (event) => {
    if (event.key === WISHLIST_STORAGE_KEY || event.key == null) this.#render();
  };

  #render() {
    const active = isInWishlist(this.handle);
    this.classList.toggle('is-active', active);

    const { button } = this.refs;
    if (!button) return;
    button.setAttribute('aria-pressed', String(active));
    button.setAttribute(
      'aria-label',
      active ? `Remove ${this.productTitle} from wishlist` : `Save ${this.productTitle} to wishlist`
    );
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}
